import React from 'react';
import { HashRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import ScrollToTop from './util/components/ScrollToTop';
// ON LOAD FUNCTIONS
import { checkPermissions } from './onLoadFunctions';

// components
import BrandedAppIndex from './pages/brandedApp';
import MerchantStoreAdd from './pages/brandedApp/merchantStoreAdd';
import MerchantStoreEdit from './pages/brandedApp/merchantStoreEdit';
import MerchantStoreDetails from './pages/brandedApp/merchantStoreDetails';
import DashboardIndex from './pages/dashboard/index';
import App from './app';
import Billing from './pages/billing/index';
import Refund from './pages/refund/Refund';
import SettingsIndex from './pages/settings/index';
import DocumentsPage from './pages/documents/index';
import { paths, settingsTabRoute } from './paths';

// Memberships
import Memberships from './pages/memberships/index';
import AddMembershipR from './pages/memberships/addMembership';
import MembershipDetail from './pages/memberships/membershipDetail';
import EditMembership from './pages/memberships/editMembership';
import UserMembershipDetails from './pages/memberships/userMembershipDetails';

// RATE V2
import RatesV2Add from './pages/ratesv2/AddRates';
import ViewRate from './pages/ratesv2/ViewRates';
import EditRate from './pages/ratesv2/EditRates';

// Fees
import AddFee from './pages/billing/fees/addFee';
import EditFee from './pages/billing/fees/editFee';
import ViewFee from './pages/billing/fees/viewFee';

// new hardware components
import HardwareR from './pages/hardware/index';
import NewHardwareAdd from './pages/hardware/hardwareAddNew';
import NewHardwareEdit from './pages/hardware/newHardwareEdit';
import NewHardwareAddFromCsv from './pages/hardware/newHardwareAddFromCsv';
import AddHardwareAssignment from './pages/hardware/addHardwareAssignment';

// user components
import UserPage from './pages/userPages/index';
import UserAdd from './pages/userPages/addUser';
import UserEdit from './pages/userPages/editUser';
import UserAddFromCsv from './pages/userPages/addUsersFromCsv';
import UserAddRfidCard from './pages/userPages/addUserRfidCard';
import UserRfidCardDetails from './pages/userPages/userDetailsAssets/userRfidCardDetail';
import EditUserRfidCard from './pages/userPages/editUserRfidCard';
import UserAddRental from './pages/userPages/rentals/addRental';

// rental components
import RentalR from './pages/rentals/rentals';

// station components
import LocationIndex from './pages/stations/index';
import StationAddR from './pages/stations/stationAdd';
import StationEditR from './pages/stations/stationEdit';

// Reported Problems component
import ProblemDetail from './pages/problems/problemDetails';

// Dispute Component
import DisputeDetails from './pages/billing/disputeDetails';

// Transactions
import TransactionDetailModal from './pages/transactions/transactionDetailModal';

// System components
// import System from './pages/systems/systems';
import AddSystem from './pages/systems/add';
import adminDetails from './pages/settings/admin/admin-details-index';
import AddAdmin from './pages/settings/admin/admin-add-index';

// Coupons
import ViewCoupon from './pages/coupons/couponDetail';
import EditCoupon from './pages/coupons/couponEdit';
import OrganizationCouponIndex from './pages/coupons/organization/couponIndex';
import OrganizationCouponDetail from './pages/coupons/organization/couponDetail';
import OrganizationCouponEdit from './pages/coupons/organization/couponEdit';
import {
  CouponAssignedUsersSystem,
  CouponAssignedUsersOrganization,
} from './components/coupons/CouponAssignedUsers';

// Maintenance
import Maintenance from './pages/maintenance/index';
import TicketDetail from './pages/maintenance/ticketdetail';
import TaskDetail from './pages/maintenance/taskdetail';
import EditTicket from './pages/maintenance/editticket';
import AddCompletedTask from './pages/maintenance/addCompletedTask';
import AddTicket from './pages/maintenance/addticket';

// MOVATIC ONLY
// Image Approval
import Images from './pages/imageApproval';
// add hardware product/version
import AddProduct from './pages/hardwareproduct/add-product';
import AddProductVersion from './pages/hardwareproduct/add-version';

// components
import LoginPage from './pages/login';

import store from './store';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from './theme';

// issuing
import AddIssuingCard from './pages/billing/issuing/issuingCardAdd';
import ProblemsOpen from './pages/problems/problems';
import withSystemId from './hoc/withSystemId';
import SupportTicketsIndex from './pages/problems';

import OrganizationSupportTicketsIndex from './pages/problems/organization';
import IndexUserDetails from './pages/userPages/indexUserDetails';
import IndexRentalDetails from './pages/rentals/indexDetails';
import IndexStationDetails from './pages/stations/indexStationDetails';
import IndexDetails from './pages/memberships/indexDetails';
import { IndexHardwareDetails } from './pages/hardware/indexHardwareDetails';
import AddOrganization from './pages/organizations/add';
import OrganizationSystemTable from './pages/settings/organization/system-table';
import SupportPage from './pages/support/index';
import SupportMetricsPage from './pages/support/metrics';
import SupportTagsPage from './pages/support/tags';
import SupportIssuesPage from './pages/support/issues/IssuesPage';
import { ChatAccess } from './pages/support/ChatAccess';

const queryClient = new QueryClient();

export default class Root extends React.Component {
  constructor(props) {
    super(props);

    this.addNotification = this.addNotification.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.error !== nextProps.error ||
      this.props.success !== nextProps.success
    ) {
      if (nextProps.error) {
        this.addNotification(nextProps.error, 'error', 'Error!');
        this.props.clearToast('CLEAR_ERROR');
      }
      if (nextProps.success) {
        this.addNotification(nextProps.success, 'success', 'Congratulations!', 8);
        this.props.clearToast('CLEAR_SUCCESS');
      }
    }
  }

  addNotification(message, type, title, dismiss = 0) {
    if (this.refs.notificationSystem) {
      this.refs.notificationSystem.addNotification({
        message,
        level: type,
        autoDismiss: dismiss,
        title,
      });
    }
  }

  render() {
    const AppRoutes = () => (
      <Switch>
        <Route exact path="/" component={LoginPage} />

        <Route path="/(resetpassword|setpassword)">
          <Switch>
            <Route exact path="/login" component={LoginPage} />
            <Route
              exact
              path="/:page(resetpassword|setpassword)/:email?/:token?"
              component={LoginPage}
            />
          </Switch>
        </Route>

        <Route>
          <App>
            <Switch>
              <Route exact path="/dashboard" component={withSystemId(DashboardIndex)} />

              {/* USERS  */}
              <Route path="/users">
                <Route
                  render={() => {
                    checkPermissions(['users']);
                  }}
                />
                <Switch>
                  <Route exact path="/users/" component={withSystemId(UserPage)} />
                  <Route path="/users/">
                    <Switch>
                      <Route exact path="/users/add" component={withSystemId(UserAdd)} />
                      <Route
                        exact
                        path="/users/add_csv"
                        component={withSystemId(UserAddFromCsv)}
                      />
                      <Route
                        exact
                        path="/users/:userPhone/edit"
                        component={withSystemId(UserEdit)}
                      />
                      <Route
                        exact
                        path="/users/:userPhone/add-rfid"
                        component={withSystemId(UserAddRfidCard)}
                      />
                      <Route
                        exact
                        path="/users/:userPhone/rfid/:cardId"
                        component={withSystemId(UserRfidCardDetails)}
                      />
                      <Route
                        exact
                        path="/users/:userPhone/rfid/:cardId/edit"
                        component={withSystemId(EditUserRfidCard)}
                      />
                      <Route
                        exact
                        path="/users/:userPhone/:currentTab?"
                        component={withSystemId(IndexUserDetails)}
                      />
                      <Route
                        exact
                        path="/users/:userPhone/rentals/add"
                        onEnter={checkPermissions(['rental'])}
                        component={withSystemId(UserAddRental)}
                      />
                      <Route render={() => <Redirect to="/users/" />} />
                    </Switch>
                  </Route>
                </Switch>
              </Route>
              {/* RENTALS */}
              <Route path="/rentals">
                <Route
                  render={() => {
                    checkPermissions(['rental']);
                  }}
                />
                <Switch>
                  <Route exact path="/rentals" component={withSystemId(RentalR)} />
                  {/*
                    Both URL shapes are handled by a single Route (passing an array
                    of paths) so the page is not unmounted/remounted when switching
                    between the default view and a tab. A remount would reset the
                    detail query's loading state and blank the whole page behind the
                    full-screen PageLoader while data refetched.
                  */}
                  <Route
                    exact
                    path={[
                      '/rentals/:rentalId/:currentTab(details|support|logs|hardware-logs|unlocks|analytics)',
                      '/rentals/:rentalId',
                    ]}
                    component={withSystemId(IndexRentalDetails)}
                  />
                </Switch>
              </Route>
            </Switch>

            {/* new */}
            <Route path="/hardware">
              <Route
                render={() => {
                  checkPermissions(['physical']);
                }}
              />
              <Switch>
                <Route
                  exact
                  path="/hardware/add"
                  component={withSystemId(NewHardwareAdd)}
                />
                <Route
                  exact
                  path="/hardware/add_csv"
                  component={withSystemId(NewHardwareAddFromCsv)}
                />
                <Route
                  exact
                  path="/hardware/:hardwareId/edit"
                  component={withSystemId(NewHardwareEdit)}
                />
                <Route
                  exact
                  path="/hardware/:hardwareId/add-assignment"
                  component={withSystemId(AddHardwareAssignment)}
                />
                <Route
                  exact
                  path="/hardware/:hardwareId/:currentTab(general|rentals|maintenance-tickets|maintenance-logs|event-logs|unlocks|diagnostics)"
                  component={withSystemId(IndexHardwareDetails)}
                />
                <Route
                  exact
                  path="/hardware/:hardwareId"
                  component={withSystemId(IndexHardwareDetails)}
                />
                <Route exact path="/hardware" component={withSystemId(HardwareR)} />
              </Switch>
            </Route>

            <Route path="/locations">
              <Route
                render={() => {
                  checkPermissions(['locations']);
                }}
              />
              <Switch>
                <Route exact path="/locations/" component={withSystemId(LocationIndex)} />
                <Route
                  exact
                  path="/locations/add"
                  component={withSystemId(StationAddR)}
                />
                <Route
                  exact
                  path="/locations/:stationId/edit"
                  component={withSystemId(StationEditR)}
                />
                <Route
                  exact
                  path="/locations/:currentTab(locations|map)?"
                  component={withSystemId(LocationIndex)}
                />
                <Route
                  exact
                  path="/locations/:stationId/:currentTab(general|rentals|media)?"
                  component={withSystemId(IndexStationDetails)}
                />
              </Switch>
            </Route>

            {/* Statements moved from a Settings tab to its own Documents page. Outside
                /settings, whose bank check would bounce admins who can read documents. */}
            <Route
              exact
              path="/settings/statements"
              render={({ location }) => (
                <Redirect to={{ pathname: paths.documents, search: location.search }} />
              )}
            />

            <Route path={paths.documents}>
              <Route
                render={() => {
                  checkPermissions(['basic']);
                }}
              />
              <Route
                exact
                path={paths.documents}
                component={withSystemId(DocumentsPage)}
              />
            </Route>

            <Route path="/settings">
              <Route
                render={() => {
                  checkPermissions(['bank']);
                }}
              />
              <Switch>
                <Route
                  exact
                  path="/settings/admin/add"
                  component={withSystemId(AddAdmin)}
                />
                <Route
                  exact
                  path="/settings/admin/:email"
                  component={withSystemId(adminDetails)}
                />
                <Route
                  exact
                  path={settingsTabRoute}
                  component={withSystemId(SettingsIndex)}
                />
              </Switch>
            </Route>

            <Route path="/problems">
              <Route
                render={() => {
                  checkPermissions(['support']);
                }}
              />
              <Switch>
                <Route
                  exact
                  path="/problems"
                  component={withSystemId(SupportTicketsIndex)}
                />
                <Route
                  exact
                  path="/problems/:currentTab(tickets|tags)?"
                  component={withSystemId(ProblemsOpen)}
                />
                <Route
                  exact
                  path="/problems/:currentTab(tickets|tags)?"
                  component={withSystemId(OrganizationSupportTicketsIndex)}
                />
                <Route
                  exact
                  path="/problems/:problemId"
                  component={withSystemId(ProblemDetail)}
                />
              </Switch>
            </Route>

            <Route path="/maintenance">
              <Route
                render={() => {
                  checkPermissions(['maintenance']);
                }}
              />
              <Switch>
                <Route
                  exact
                  path="/maintenance/add"
                  component={withSystemId(AddTicket)}
                />
                <Route
                  exact
                  path="/maintenance/add/report/:reportId"
                  component={withSystemId(AddTicket)}
                />
                <Route
                  exact
                  path="/maintenance/add/unit/:hardwareId"
                  component={withSystemId(AddTicket)}
                />
                <Route
                  exact
                  path="/maintenance/:currentTab(tickets|logs|tags)?"
                  component={withSystemId(Maintenance)}
                />
                <Route
                  exact
                  path="/maintenance/log/add/unit/:hardwareId"
                  component={withSystemId(AddCompletedTask)}
                />
                <Route
                  exact
                  path="/maintenance/log/add"
                  component={withSystemId(AddCompletedTask)}
                />
                <Route
                  exact
                  path="/maintenance/log/:taskId"
                  component={withSystemId(TaskDetail)}
                />
                <Route
                  exact
                  path="/maintenance/edit/:ticketId"
                  component={withSystemId(EditTicket)}
                />
                <Route
                  exact
                  path="/maintenance/:ticketId"
                  component={withSystemId(TicketDetail)}
                />
                <Route
                  exact
                  path="/maintenance/close/:ticketId"
                  component={withSystemId(AddCompletedTask)}
                />
              </Switch>
            </Route>

            <Route path="/memberships">
              <Route
                render={() => {
                  checkPermissions(['memberships']);
                }}
              />
              <Switch>
                <Route
                  exact
                  path="/memberships/add"
                  component={withSystemId(AddMembershipR)}
                />
                <Route
                  exact
                  path="/memberships/user/:membershipId"
                  component={withSystemId(UserMembershipDetails)}
                />
                <Route
                  exact
                  path="/memberships/edit/:membershipId"
                  component={withSystemId(EditMembership)}
                />
                <Route
                  exact
                  path="/memberships/:currentTab(memberships|pending)?"
                  component={withSystemId(Memberships)}
                />
                <Route
                  exact
                  path="/memberships/:membershipId"
                  component={withSystemId(MembershipDetail)}
                />
                <Route
                  exact
                  path="/memberships/:membershipId/:currentTab(general|members)?"
                  component={withSystemId(IndexDetails)}
                />
              </Switch>
            </Route>

            <Route path="/billing" onEnter={checkPermissions(['rates', 'refunds'])}>
              <Route
                render={() => {
                  checkPermissions(['rates', 'refunds']);
                }}
              />
              <Switch>
                <Route
                  exact
                  path="/billing/:currentTab(overview|rates|fees|coupons|transactions|disputes|disputesWarning|pending|issuing|issuingbalance)?"
                  component={withSystemId(Billing)}
                />
                <Route
                  exact
                  path="/billing/rates/add"
                  component={withSystemId(RatesV2Add)}
                />
                <Route
                  exact
                  path="/billing/rates/:rateId"
                  component={withSystemId(ViewRate)}
                />
                <Route
                  exact
                  path="/billing/rates/:rateId/edit"
                  component={withSystemId(EditRate)}
                />
                <Route exact path="/billing/fees/add" component={withSystemId(AddFee)} />
                <Route
                  exact
                  path="/billing/fees/:feeId"
                  component={withSystemId(ViewFee)}
                />
                <Route
                  exact
                  path="/billing/fees/:feeId/edit"
                  component={withSystemId(EditFee)}
                />
                <Route
                  exact
                  path="/billing/coupon/edit/:couponId"
                  component={withSystemId(EditCoupon)}
                />
                <Route
                  exact
                  path="/billing/coupon/:couponId/assignments"
                  component={withSystemId(CouponAssignedUsersSystem)}
                />
                <Route
                  exact
                  path="/billing/coupon/:couponId"
                  component={withSystemId(ViewCoupon)}
                />
                <Route
                  exact
                  path="/billing/transactions/:transactionId"
                  component={withSystemId(TransactionDetailModal)}
                />
                <Route
                  exact
                  path="/billing/transactions/detail/:transactionId"
                  component={withSystemId(TransactionDetailModal)}
                />
                <Route
                  exact
                  path="/billing/disputes/:disputeId"
                  component={withSystemId(DisputeDetails)}
                />
                <Route
                  exact
                  path="/billing/issuing/add"
                  component={withSystemId(AddIssuingCard)}
                />
              </Switch>
            </Route>
            <Route path="/refund">
              <Route
                render={() => {
                  checkPermissions(['refunds']);
                }}
              />
              <Route exact path="/refund/:rentalId" component={withSystemId(Refund)} />
            </Route>

            {/*<Route exact path="/systems" component={System} />*/}

            <Route path="/(approve-image|addproduct|addversion)">
              <Route
                render={() => {
                  checkPermissions(['movaticAccess']);
                }}
              />
              <Switch>
                <Route exact path="/approve-image" component={withSystemId(Images)} />
                <Route exact path="/addproduct" component={withSystemId(AddProduct)} />
                <Route
                  exact
                  path="/addversion"
                  component={withSystemId(AddProductVersion)}
                />
              </Switch>
            </Route>

            <Route path="/addSystem">
              <Route
                render={() => {
                  checkPermissions(['partner']);
                }}
              />
              <Route exact path="/addSystem" component={withSystemId(AddSystem)} />
            </Route>

            <Route path="/addOrganization">
              <Route
                render={() => {
                  checkPermissions(['partner']);
                }}
              />
              <Route
                exact
                path="/addOrganization"
                component={withSystemId(AddOrganization)}
              />
            </Route>

            <Route path="/support">
              <ChatAccess>
                {/* Global: the queue is scoped by the token, not by the selected system. */}
                {/*
                The queue and a conversation opened by its link (Slack alerts,
                Copy link) share one Route: the page keeps its socket, its
                queue and its heartbeat when the URL moves from one to the other.
              */}
                <Route
                  exact
                  path={[paths.support, paths.supportChat]}
                  component={SupportPage}
                />
                <Route exact path="/support/metrics" component={SupportMetricsPage} />
                <Route exact path={paths.supportTags} component={SupportTagsPage} />
                <Route exact path={paths.supportIssues} component={SupportIssuesPage} />
              </ChatAccess>
            </Route>

            <Route path="/systems">
              <Route
                render={() => {
                  checkPermissions(['bank']);
                }}
              />
              <Route
                exact
                path="/systems"
                component={withSystemId(OrganizationSystemTable)}
              />
            </Route>

            <Route path="/branded-app">
              <Switch>
                <Route
                  exact
                  path="/branded-app/merchant-locations/add"
                  component={withSystemId(MerchantStoreAdd)}
                />
                <Route
                  exact
                  path="/branded-app/merchant-locations/:merchantStoreId/edit"
                  component={withSystemId(MerchantStoreEdit)}
                />
                <Route
                  exact
                  path="/branded-app/merchant-locations/:merchantStoreId/:currentTab(general)?"
                  component={withSystemId(MerchantStoreDetails)}
                />
                <Route
                  exact
                  path="/branded-app/:currentTab(merchant-locations)?"
                  component={withSystemId(BrandedAppIndex)}
                />
              </Switch>
            </Route>

            <Route path="/organization/billing">
              <Switch>
                <Route
                  exact
                  path="/organization/billing/coupons"
                  component={withSystemId(OrganizationCouponIndex)}
                />
                <Route
                  exact
                  path="/organization/billing/coupon/edit/:couponId"
                  component={withSystemId(OrganizationCouponEdit)}
                />
                <Route
                  exact
                  path="/organization/billing/coupon/:couponId/assignments"
                  component={withSystemId(CouponAssignedUsersOrganization)}
                />
                <Route
                  exact
                  path="/organization/billing/coupon/:couponId"
                  component={withSystemId(OrganizationCouponDetail)}
                />
              </Switch>
            </Route>
          </App>
        </Route>
      </Switch>
    );
    const theme = createTheme({
      colorPreset: 'blue',
      contrast: 'normal',
      direction: 'ltr',
      paletteMode: 'dark',
      responsiveFontSizes: true,
    });
    return (
      <Router>
        <React.StrictMode>
          <ScrollToTop />
          <Provider store={store}>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={theme}>
                <AppRoutes />
              </ThemeProvider>
            </QueryClientProvider>
          </Provider>
        </React.StrictMode>
      </Router>
    );
  }
}
